import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Fill widths that decay, the way a ranked breakdown actually looks. */
const FILL_WIDTHS = ['86%', '64%', '48%', '33%', '21%'] as const;
const LABEL_WIDTHS = ['48%', '62%', '38%', '54%', '44%'] as const;

interface ProgressListSkeletonProps {
    readonly rows?: number;
    /** Track height in px — 8 matches the `h-2` bars used across the dashboard. */
    readonly barHeight?: number;
    /** Draws a trailing count/percentage beside each label. */
    readonly showValue?: boolean;
    readonly className?: string;
}

/**
 * Loading placeholder for a labelled progress-bar list — poll option
 * breakdowns, plan-usage rows, setup checklists.
 *
 * The fills decay rather than sitting uniform: a stack of equal bars reads as a
 * loading graphic, while a descending one reads as the ranked breakdown that is
 * about to arrive.
 *
 * The track is the real `bg-bg-raised`, so the fill inside carries
 * `skeleton-on-raised` — at the default ramp it would be invisible against it.
 */
export function ProgressListSkeleton({
    rows = 4,
    barHeight = 8,
    showValue = true,
    className,
}: ProgressListSkeletonProps) {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-3">
                        <SkeletonText size="xs" width={LABEL_WIDTHS[i % LABEL_WIDTHS.length]} />
                        {showValue && <SkeletonText size="xs" width="52px" />}
                    </div>
                    <div
                        className="skeleton-on-raised w-full overflow-hidden rounded-full bg-bg-raised"
                        style={{ height: barHeight }}
                    >
                        <Skeleton
                            className="h-full rounded-full"
                            style={{ width: FILL_WIDTHS[i % FILL_WIDTHS.length] }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}
